import React, { useState, useEffect, useRef } from 'react';
import {
  Monitor,
  X,
  Maximize2,
  Minimize2,
  RefreshCw,
  Download,
  Upload,
  Mouse,
  Keyboard,
  Eye,
  EyeOff,
  Settings,
  Wifi,
  WifiOff,
  Loader2,
  AlertTriangle,
  Power,
  RotateCw,
  Terminal,
  ExternalLink
} from 'lucide-react';
import socket from '../services/socket';

export default function RemoteDesktop({ device, onClose }) {
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [screenshot, setScreenshot] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [quality, setQuality] = useState('medium'); // low, medium, high
  const [refreshRate, setRefreshRate] = useState(1000); // ms
  const [showMouseControl, setShowMouseControl] = useState(true);
  const [remoteMethod, setRemoteMethod] = useState('native'); // native, anydesk, teamviewer, splashtop
  const [externalUrl, setExternalUrl] = useState(null);
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    // Solicitar inicio de sesión remota
    socket.emit('remote:start', {
      deviceId: device.machine_id || device.id,
      quality,
      method: remoteMethod
    });

    // Escuchar respuesta de inicio
    socket.on('remote:started', (data) => {
      console.log('Sesión remota iniciada:', data);
      setConnected(true);
      setLoading(false);

      if (data.method === 'external' && data.url) {
        setExternalUrl(data.url);
        setRemoteMethod('external');
      }
    });

    // Escuchar capturas de pantalla
    socket.on('remote:screenshot', (data) => {
      if (data.deviceId === device.id || data.machineId === device.machine_id) {
        setScreenshot(data.screenshot);
        drawScreenshot(data.screenshot);
      }
    });

    // Escuchar errores
    socket.on('remote:error', (error) => {
      console.error('Error en sesión remota:', error);
      setLoading(false);
      alert('Error al iniciar sesión remota: ' + error.message);
    });

    // Iniciar captura periódica si es método nativo
    if (remoteMethod === 'native') {
      intervalRef.current = setInterval(() => {
        if (connected) {
          socket.emit('remote:request-screenshot', {
            deviceId: device.machine_id || device.id
          });
        }
      }, refreshRate);
    }

    return () => {
      // Detener sesión remota al cerrar
      socket.emit('remote:stop', { deviceId: device.machine_id || device.id });
      socket.off('remote:started');
      socket.off('remote:screenshot');
      socket.off('remote:error');
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [device, quality, refreshRate, remoteMethod]);

  const drawScreenshot = (imageData) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
    };

    img.src = imageData; // Base64 data URL
  };

  const handleMouseClick = (e) => {
    if (!showMouseControl || !connected || remoteMethod !== 'native') return;

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();

    const x = Math.floor((e.clientX - rect.left) / rect.width * canvas.width);
    const y = Math.floor((e.clientY - rect.top) / rect.height * canvas.height);

    socket.emit('remote:mouse-click', {
      deviceId: device.machine_id || device.id,
      x,
      y,
      button: e.button // 0: left, 1: middle, 2: right
    });
  };

  const handleMouseMove = (e) => {
    if (!showMouseControl || !connected || remoteMethod !== 'native') return;

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();

    const x = Math.floor((e.clientX - rect.left) / rect.width * canvas.width);
    const y = Math.floor((e.clientY - rect.top) / rect.height * canvas.height);

    // Throttle mouse move events
    if (!handleMouseMove.lastEmit || Date.now() - handleMouseMove.lastEmit > 50) {
      socket.emit('remote:mouse-move', {
        deviceId: device.machine_id || device.id,
        x,
        y
      });
      handleMouseMove.lastEmit = Date.now();
    }
  };

  const handleKeyPress = (e) => {
    if (!connected || remoteMethod !== 'native') return;

    socket.emit('remote:keyboard', {
      deviceId: device.machine_id || device.id,
      key: e.key,
      code: e.code,
      ctrlKey: e.ctrlKey,
      shiftKey: e.shiftKey,
      altKey: e.altKey
    });
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      if (containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
    setIsFullscreen(!isFullscreen);
  };

  const sendCommand = (command) => {
    socket.emit('panel:send-command', {
      machineId: device.machine_id || device.id,
      command,
      params: {}
    });
  };

  const openExternalRemote = (tool) => {
    // Generar URL de conexión externa
    let url = null;
    switch (tool) {
      case 'anydesk':
        url = `anydesk:${device.anydesk_id || 'unknown'}`;
        break;
      case 'teamviewer':
        url = `teamviewer10://control?device=${device.teamviewer_id || 'unknown'}`;
        break;
      case 'splashtop':
        url = `https://my.splashtop.com/`;
        break;
      default:
        break;
    }

    if (url) {
      window.open(url, '_blank');
    } else {
      alert(`${tool} no está configurado en este dispositivo`);
    }
  };

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 bg-black z-50 flex flex-col"
    >
      {/* Header */}
      <div className="bg-gray-900 text-white px-4 py-3 flex items-center justify-between border-b border-gray-700">
        <div className="flex items-center gap-3">
          <Monitor className="w-5 h-5 text-blue-400" />
          <div>
            <h2 className="font-semibold">{device.device_name || device.taquillaId}</h2>
            <p className="text-xs text-gray-400">{device.device_id || device.machineId}</p>
          </div>
          <div className="flex items-center gap-2 ml-4">
            {connected ? (
              <>
                <Wifi className="w-4 h-4 text-green-400" />
                <span className="text-xs text-green-400">Conectado</span>
              </>
            ) : (
              <>
                <WifiOff className="w-4 h-4 text-gray-400" />
                <span className="text-xs text-gray-400">Desconectado</span>
              </>
            )}
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-2">
          {/* Remote Method Selector */}
          <select
            value={remoteMethod}
            onChange={(e) => setRemoteMethod(e.target.value)}
            className="px-3 py-1 bg-gray-800 border border-gray-700 rounded text-sm"
            disabled={connected}
          >
            <option value="native">Nativo (WebSocket)</option>
            <option value="anydesk">AnyDesk</option>
            <option value="teamviewer">TeamViewer</option>
            <option value="splashtop">Splashtop</option>
          </select>

          {remoteMethod === 'native' && (
            <>
              {/* Quality */}
              <select
                value={quality}
                onChange={(e) => setQuality(e.target.value)}
                className="px-3 py-1 bg-gray-800 border border-gray-700 rounded text-sm"
              >
                <option value="low">Baja calidad</option>
                <option value="medium">Media calidad</option>
                <option value="high">Alta calidad</option>
              </select>

              {/* Mouse Control */}
              <button
                onClick={() => setShowMouseControl(!showMouseControl)}
                className={`p-2 rounded ${showMouseControl ? 'bg-blue-600' : 'bg-gray-700'}`}
                title={showMouseControl ? 'Control activado' : 'Control desactivado'}
              >
                {showMouseControl ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              </button>
            </>
          )}

          {/* Quick Actions */}
          <div className="flex items-center gap-1 border-l border-gray-700 pl-2 ml-2">
            <button
              onClick={() => sendCommand('restart')}
              className="p-2 hover:bg-gray-700 rounded"
              title="Reiniciar"
            >
              <RotateCw className="w-4 h-4" />
            </button>
            <button
              onClick={() => sendCommand('open-cmd')}
              className="p-2 hover:bg-gray-700 rounded"
              title="Abrir CMD"
            >
              <Terminal className="w-4 h-4" />
            </button>
          </div>

          {/* Fullscreen */}
          <button
            onClick={toggleFullscreen}
            className="p-2 hover:bg-gray-700 rounded"
          >
            {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>

          {/* Close */}
          <button
            onClick={onClose}
            className="p-2 hover:bg-red-600 rounded"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden bg-gray-950 relative">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-900 bg-opacity-90 z-10">
            <div className="text-center">
              <Loader2 className="w-12 h-12 animate-spin text-blue-500 mx-auto mb-4" />
              <p className="text-white">Conectando con dispositivo...</p>
              <p className="text-gray-400 text-sm mt-2">Método: {remoteMethod}</p>
            </div>
          </div>
        )}

        {!loading && !connected && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center text-gray-400">
              <AlertTriangle className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p>No se pudo conectar con el dispositivo</p>
              <p className="text-sm mt-2">El dispositivo debe estar online y tener Xavia Client instalado</p>
            </div>
          </div>
        )}

        {remoteMethod === 'native' ? (
          // Native WebSocket streaming
          <div className="h-full flex items-center justify-center p-4">
            <canvas
              ref={canvasRef}
              onClick={handleMouseClick}
              onMouseMove={handleMouseMove}
              onKeyDown={handleKeyPress}
              tabIndex={0}
              className="max-w-full max-h-full border border-gray-700 rounded shadow-2xl"
              style={{
                cursor: showMouseControl ? 'crosshair' : 'default',
                imageRendering: quality === 'high' ? 'auto' : 'pixelated'
              }}
            />
          </div>
        ) : externalUrl ? (
          // External method with iframe (if available)
          <div className="h-full p-4">
            <div className="h-full bg-white rounded">
              <iframe
                src={externalUrl}
                className="w-full h-full border-0"
                title="Escritorio Remoto"
              />
            </div>
          </div>
        ) : (
          // External method instructions
          <div className="h-full flex items-center justify-center">
            <div className="text-center max-w-lg">
              <ExternalLink className="w-16 h-16 mx-auto mb-4 text-blue-500" />
              <h3 className="text-xl font-bold text-white mb-4">
                Escritorio Remoto Externo
              </h3>
              <p className="text-gray-300 mb-6">
                {remoteMethod === 'anydesk' && 'Abre AnyDesk e introduce el ID del dispositivo'}
                {remoteMethod === 'teamviewer' && 'Abre TeamViewer e introduce el ID del dispositivo'}
                {remoteMethod === 'splashtop' && 'Abre Splashtop Business y busca el dispositivo'}
              </p>

              {device.anydesk_id && remoteMethod === 'anydesk' && (
                <div className="bg-gray-800 rounded-lg p-4 mb-4">
                  <p className="text-gray-400 text-sm mb-1">ID de AnyDesk:</p>
                  <p className="text-2xl font-mono text-white">{device.anydesk_id}</p>
                </div>
              )}

              {device.teamviewer_id && remoteMethod === 'teamviewer' && (
                <div className="bg-gray-800 rounded-lg p-4 mb-4">
                  <p className="text-gray-400 text-sm mb-1">ID de TeamViewer:</p>
                  <p className="text-2xl font-mono text-white">{device.teamviewer_id}</p>
                </div>
              )}

              <button
                onClick={() => openExternalRemote(remoteMethod)}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium inline-flex items-center gap-2"
              >
                <ExternalLink className="w-5 h-5" />
                Abrir {remoteMethod}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Footer Status */}
      {connected && remoteMethod === 'native' && (
        <div className="bg-gray-900 text-xs text-gray-400 px-4 py-2 flex items-center justify-between border-t border-gray-700">
          <div className="flex items-center gap-4">
            <span>Calidad: {quality}</span>
            <span>Tasa refresco: {refreshRate}ms</span>
            <span>Control: {showMouseControl ? 'Activado' : 'Desactivado'}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-green-400">● Streaming activo</span>
          </div>
        </div>
      )}
    </div>
  );
}
